/**
 * Vector maths for continuous entity positions.
 *
 * Entities hold float `x`/`y`; the nav grid is only a coarse index into
 * walkability (see `./grid.ts`). Everything here is immutable and framework
 * free: a `Vec2` is a plain object, so it survives JSON, `structuredClone` and
 * the wire without a codec, and no function ever writes into its arguments —
 * aliasing bugs in movement code are invisible until they desync a client.
 *
 * Units are grid cells throughout. A caller that thinks in pixels scales at the
 * rendering boundary, never in here.
 */
/** The origin. Shared rather than reallocated: nothing may mutate a `Vec2`. */
export const VEC2_ZERO = { x: 0, y: 0 };
export const vec2 = (x, y) => ({ x, y });
export const add = (a, b) => ({ x: a.x + b.x, y: a.y + b.y });
/** The displacement that carries `b` onto `a`, i.e. `a - b`. */
export const subtract = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });
export const scale = (v, factor) => ({ x: v.x * factor, y: v.y * factor });
export const dot = (a, b) => a.x * b.x + a.y * b.y;
/** Squared length. Prefer it wherever only the comparison matters — no square root. */
export const magnitudeSquared = (v) => v.x * v.x + v.y * v.y;
export const magnitude = (v) => Math.sqrt(magnitudeSquared(v));
/** Squared distance. The hot-loop form: A* and flow fields compare, they do not measure. */
export const distanceSquared = (a, b) => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    return dx * dx + dy * dy;
};
export const distance = (a, b) => Math.sqrt(distanceSquared(a, b));
/**
 * The unit vector along `v`, or the origin when `v` has no length.
 *
 * Returning the origin rather than a NaN pair is deliberate: two entities on
 * the exact same spot is an ordinary game state, and a NaN would spread
 * silently through every position it touched.
 */
export const normalize = (v) => {
    const length = magnitude(v);
    return length === 0 ? VEC2_ZERO : { x: v.x / length, y: v.y / length };
};
/** Unit vector pointing from `from` at `to`; the origin when they coincide. */
export const directionTo = (from, to) => normalize(subtract(to, from));
/**
 * Linear interpolation, unclamped — `t` outside [0, 1] extrapolates on purpose.
 *
 * Written as `(1 - t)·a + t·b` rather than `a + (b - a)·t` because only that
 * form returns the endpoints *exactly* at `t = 0` and `t = 1`; the other drifts
 * by an ulp, and arrival at a waypoint is an equality test.
 */
export const lerp = (a, b, t) => ({
    x: (1 - t) * a.x + t * b.x,
    y: (1 - t) * a.y + t * b.y,
});
/**
 * Moves `from` at most `maxStep` towards `to`, landing exactly on `to` rather
 * than overshooting it.
 *
 * This is the whole of fixed-step movement: at 20 Hz the caller passes
 * `speed / 20` and never accumulates a variable `dt`, so a replay of the same
 * inputs walks the same line cell for cell.
 */
export const moveTowards = (from, to, maxStep) => {
    // Phrased as a negated comparison so NaN — a poisoned `dt` — is rejected too.
    if (!(maxStep >= 0)) {
        throw new RangeError(`moveTowards expects a non-negative step, received ${maxStep}`);
    }
    const remaining = distance(from, to);
    if (remaining <= maxStep)
        return to;
    return add(from, scale(subtract(to, from), maxStep / remaining));
};
//# sourceMappingURL=geometry.js.map